/* eslint-disable @typescript-eslint/no-unused-vars */
import { useContext } from 'react';
import { FormikHelpers } from 'formik';
import { useRouter } from 'next/router';
import { AUTH_ERRORS } from '../../../Constants/ErrorMessages';
import {
  DEEPLINK_CHALLENGE_ID,
  IS_USER_AUTHENTICATED,
  LogInResponseCode,
  ROUTES,
  STORAGE_PROJECT_ID,
} from '../../../Constants/variables';
import useErrorHandlers from '../../../hooks/useErrorHandlers';
import utils, { logger } from '../../../utils';
import helpers from '../../../utils/helpers';
import AuthContext from '../../../AppContext/AuthProvider';
import useAsyncTask from '../../../hooks/useAsyncTask';
import { LoginFormData } from '../../../models/auth';
import { sendEvent } from '../../../hooks/useAnalytics';
import ClassroomModel from '../../../models/Classroom';
import useAuthActions from '../useAuthActions';

function useLoginActions() {
  // eslint-disable-next-line unused-imports/no-unused-vars
  const { login, setAuthState, challengeJoinedCheck, challengeRedirect } = useContext(AuthContext);
  const router = useRouter();
  const { handleError } = useErrorHandlers();

  // const challengeJoinedCheck = async (challengeId: string) => {
  //   try {
  //     const { data } = await ProjectModel.isChallengeJoined(challengeId);
  //     return data.projectId;
  //   } catch (error) {
  //     logger.error(error);
  //     throw error;
  //   }
  // };
  const joinAndGoToClassroom = async (values: any) => {
    if (localStorage.getItem('classCode') !== null && values) {
      await ClassroomModel.joinClassroom({
        class_code: localStorage.getItem('classCode'),
        email: values.email,
      });
      const classroomId = localStorage.getItem('classroomId');
      if (classroomId) {
        localStorage.removeItem('classCode');
        localStorage.removeItem('classroomId');
        router.push(
          helpers.generatePath(ROUTES.JOIN_CLASSROOM, {
            classroomId,
          }),
        );
      }
    }
  };

  const handleLogin = useAsyncTask<{
    values: Partial<LoginFormData> | null;
    formikHelpers?: FormikHelpers<Partial<LoginFormData>>;
    previousLogin?: boolean;
  }>(async data => {
    const { values, formikHelpers } = data;

    try {
      let loginType;
      let response;
      if (values && values.sso_id) {
        response = await login({
          email: values.email,
          password: values.password,
          sso_id: values.sso_id,
        });
      } else if (values && values.password) {
        response = await login({
          email: values.email,
          password: values.password,
        });
        loginType = 'email';
      }
      if (!loginType) {
        loginType = 'sso';
      }
      // const appUser = await AuthModel.getUser().then((data) => {
      //   return data.data;
      // });

      if (response?.responseCode === LogInResponseCode.TWO_FACTOR_AUTH_REQUIRED) {
        return null;
      }

      sendEvent('login', { login_type: loginType, nonInteraction: true });

      utils.setCookie('isUserAuthenticated', IS_USER_AUTHENTICATED.YES);
      const projectId = utils.getCookie(STORAGE_PROJECT_ID);
      const deepLinkChallengeId = utils.getCookie(DEEPLINK_CHALLENGE_ID);
      const challengeUrl = localStorage.getItem('last_url');
      const challengeId = localStorage.getItem('challengeId');
      if (localStorage.getItem('classCode') && values) {
        return await joinAndGoToClassroom(values);
      }
      if (projectId && !challengeUrl) {
        router.push(
          helpers.generatePath(ROUTES.PROJECT, {
            projectId,
          }),
        );
        return null;
      }
      if (deepLinkChallengeId && challengeJoinedCheck) {
        const projectId = await challengeJoinedCheck(deepLinkChallengeId);
        projectId
          ? router.push(
              helpers.generatePath(ROUTES.PROJECT, {
                projectId,
              }),
            )
          : router.push(ROUTES.DASHBOARD);
        utils.clearCookie(DEEPLINK_CHALLENGE_ID);
        return null;
      }
      if (challengeUrl && challengeId && challengeRedirect) {
        await challengeRedirect(challengeId, challengeUrl);
      } else if (localStorage.getItem('partnerUserId') && values) {
        return await useAuthActions.partnerRedirectToActivity(values.email);
      } else {
        router.push(ROUTES.DASHBOARD);
      }
    } catch (error: any) {
      setAuthState('sso', null);
      setAuthState('redirected', null);
      const responseCode = error?.response?.data?.responseCode;
      if (responseCode === AUTH_ERRORS.EMAIL_NOT_VERIFIED && values) {
        return handleError({
          code: AUTH_ERRORS.EMAIL_NOT_VERIFIED,
          metaData: values.email,
        });
      }
      if (formikHelpers) {
        if (responseCode === 'USER_BLOCKED') {
          return formikHelpers.setErrors({ email: AUTH_ERRORS.USER_BLOCKED });
        }
        if (responseCode === 'INVALID_CREDENTIALS') {
          return formikHelpers.setErrors({
            password: AUTH_ERRORS.LOGIN_FAILED,
          });
        }
      }
      logger.error(error);
      throw new Error(AUTH_ERRORS.GENERIC_ERROR_500);
    }
    return null;
  });
  return { handleLogin };
}

export default useLoginActions;
